/**
 * API client — fetch wrapper with X-Device-Id + Bearer injection and
 * error mapping.
 *
 * Responsibilities:
 *  - Base URL from EXPO_PUBLIC_API_URL (defaults to local wrangler dev)
 *  - Add Authorization: Bearer <token> (plan 16 account session) to every
 *    request when a session exists; 401 responses clear the stale session
 *  - Add X-Device-Id header (telemetry only since plan 16) to every request
 *  - Parse non-2xx responses into ApiError with server's `error` code
 *  - Keep the surface small so screen-level code stays focused on the
 *    actual feature logic
 *
 * NOT here:
 *  - React Query / TanStack Query (deferred — task #8 in plan §6.1)
 *  - Retry, backoff, or offline queue
 */

import { getOrCreateDeviceId } from '../device-id';
import { clearAuthSession, getAuthToken } from '../auth';

const DEFAULT_API_BASE_URL = 'http://127.0.0.1:8787';
/** Exported untuk caller non-JSON (mis. fetchWorksheetPdfBlob). */
export const API_BASE_URL =
  process.env.EXPO_PUBLIC_API_URL?.replace(/\/+$/, '') || DEFAULT_API_BASE_URL;
export const APP_ENV = process.env.EXPO_PUBLIC_APP_ENV ?? 'local';
export const IS_REMOTE_DEV = APP_ENV === 'remote-dev';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly body?: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export type ApiFetchOptions = Omit<RequestInit, 'body'> & {
  /** Override the auto-attached X-Device-Id (for tests). */
  deviceId?: string;
  /** JSON body. Will be stringified and Content-Type: application/json set. */
  body?: unknown;
  /**
   * Phase 5.2: Parent Zone session token. When set, the X-Parent-Session
   * header is attached (in addition to X-Device-Id). Required for
   * settings + history/export + PIN change/delete endpoints.
   */
  sessionToken?: string;
};

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { deviceId, body, headers, sessionToken, ...rest } = options;

  const finalDeviceId = deviceId ?? getOrCreateDeviceId();
  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Device-Id': finalDeviceId,
    ...((headers as Record<string, string> | undefined) ?? {}),
  };
  const authToken = getAuthToken();
  if (authToken && !finalHeaders['Authorization']) {
    finalHeaders['Authorization'] = `Bearer ${authToken}`;
  }
  if (sessionToken) {
    finalHeaders['X-Parent-Session'] = sessionToken;
  }

  const init: RequestInit = {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  };

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    const message =
      err instanceof Error
        ? `Network error: ${err.message}`
        : 'Network error: unable to reach server';
    throw new ApiError(message, 0, 'NetworkError');
  }

  // 204 No Content or empty body
  const text = await response.text();
  const parsed = text.length > 0 ? safeJsonParse(text) : undefined;

  if (!response.ok) {
    const errBody = parsed as { error?: unknown; message?: unknown } | undefined;
    // Server error codes are plain strings; a ZodError body carries an
    // object instead — only keep string codes, never leak it to the UI.
    const code = typeof errBody?.error === 'string' ? errBody.error : undefined;
    // Plan 16: sesi yang sudah ditolak server (expired/revoked) tidak akan
    // pernah valid lagi — buang dari storage supaya guard redirect ke login.
    // Wrong PIN is an application error from an authenticated parent session,
    // not an expired account bearer token.
    if (response.status === 401 && authToken && code !== 'wrong_pin' && code !== 'wrong_current_pin') {
      clearAuthSession();
    }
    // Never surface a raw "HTTP NNN" to the user — map every status to a
    // friendly Indonesian message.
    const message =
      typeof errBody?.message === 'string'
        ? errBody.message
        : response.status === 400
        ? 'Data tidak valid. Muat ulang lalu coba lagi.'
        : response.status === 401
        ? 'Sesi berakhir. Muat ulang halaman.'
        : response.status === 404
        ? 'Tidak ditemukan.'
        : response.status === 429
        ? 'Terlalu banyak permintaan. Coba lagi sebentar.'
        : response.status >= 500
        ? 'Server bermasalah. Coba lagi sebentar.'
        : 'Terjadi kesalahan. Coba lagi.';
    throw new ApiError(message, response.status, code, parsed);
  }

  return parsed as T;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
